if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
        this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
        window.addEventListener('on:debounced-resize', this.zoomInitHandler);
        this.initZoom();
        this.zoomLinks.forEach((el) => {
          el.addEventListener('click', (evt) => {
            evt.preventDefault();
          });
        });
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        const selector1 = optionSelectors[this.getMediaGroupData().groupOptionIndex + 1];

        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }

        let color = selector?.querySelector('input:checked').value ?? null;
        let size = selector1?.querySelector('input:checked').value ?? null;
        let obj = {
          color: color,
          size: size
        }
        
        return obj;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap(size, color) {
      this.mediaGroupMap = { groups: {} };

      const variantMediaData = this.getMediaGroupData();
      this.viewerItems = this.querySelectorAll('.media-viewer__item');

      // Step 1: Find the SKU for the selected color from the swatch/variant entry.
      // The swatch entry is the signal — it is NOT a gallery image.
      let activeSku = null;
      for (let i = 0; i < variantMediaData.variantMedia.length; i++) {
        const entry = variantMediaData.variantMedia[i];
        if (entry.option === color && (size === null || entry.size === size || entry.size === null)) {
          activeSku = entry.sku;
          break;
        }
      }

      if (!activeSku) return this.mediaGroupMap;

      // Step 2: Initialize the group
      this.mediaGroupMap.groups[color] = {
        name: color,
        size: size,
        items: [],
        videoItems: []
      };

      const seenFilenames = new Set();

      this.viewerItems.forEach((item) => {
        // Skip the swatch viewer item entirely — it is never a gallery image.
        // Identify it as the item whose mediaId matches the variantMedia entry for this color.
        const isSwatchItem = variantMediaData.variantMedia.some(
          entry => entry.option === color && parseInt(item.dataset.mediaId, 10) === entry.mediaId
            && (entry.sku + '_STIT_SW') // filename contains SW pattern
            && item.querySelector('img')?.getAttribute('alt')?.toLowerCase().includes('swatch')
        );
        if (isSwatchItem) return;

        const isVideo = item.dataset.mediaType === 'video' || item.querySelector('video, .deferred-media');
        const match = item.innerHTML.match(/\/cdn\/shop\/(?:files|products)\/([^"?]+)/);
        const filename = match ? match[1] : null;
        const imageElement = item.querySelector('img.product-image');
        const altText = imageElement ? imageElement.getAttribute('alt') : '';

        // Step 3: Match by SKU in filename (images) or SKU/_ALL in alt text (videos)
        const matchesSku = filename && filename.includes(activeSku);
        const matchesAll = altText.includes('_ALL');
        const matchesSkuAlt = altText.includes(activeSku);

        let shouldInclude = false;
        if (isVideo) {
          shouldInclude = matchesSkuAlt || matchesAll;
        } else {
          shouldInclude = matchesSku && !seenFilenames.has(filename);
        }

        if (!shouldInclude) return;

        if (filename) seenFilenames.add(filename);

        const groupItem = { main: item };
        if (this.thumbs) {
          groupItem.thumb = this.thumbs.querySelector(
            `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
          );
        }

        if (isVideo) {
          this.mediaGroupMap.groups[color].videoItems.push(groupItem);
        } else {
          this.mediaGroupMap.groups[color].items.push(groupItem);
        }
      });

      // Step 4: Insert videos at position 3 (index 2)
      const group = this.mediaGroupMap.groups[color];
      if (group.videoItems && group.videoItems.length > 0) {
        group.videoItems.forEach((videoItem, index) => {
          group.items.splice(2 + index, 0, videoItem);
        });
      }

      // Step 5: Apply display/order to matched items
      group.items.forEach((groupItem, index) => {
        if (groupItem.main) {
          groupItem.main.style.order = index;
          groupItem.main.style.display = 'block';
        }
        if (groupItem.thumb) {
          groupItem.thumb.style.order = index;
          groupItem.thumb.style.display = 'block';
        }
      });

      // Step 6: Hide everything not in the active group
      this.viewerItems.forEach(item => {
        const isMatched = group.items.some(groupItem => groupItem.main === item);
        if (!isMatched) {
          item.style.display = 'none';
          item.style.order = '';
        }
      });

      if (this.thumbs) {
        this.thumbs.querySelectorAll('.media-thumbs__item').forEach(thumb => {
          const isMatched = group.items.some(groupItem => groupItem.thumb === thumb);
          if (!isMatched) {
            thumb.style.display = 'none';
            thumb.style.order = '';
          }
        });
      }

      // Step 7: groupFromItem helper
      this.mediaGroupMap.groupFromItem = (item) => {
        return this.mediaGroupMap.groups[color];
      };

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap(groupName.size, groupName.color);
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName.color]
        : mediaGroupMap.groupFromItem(this.currentItem);
      
      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;

          this.viewerItems.forEach((item) => {
            const imageElement = item.querySelector('img.product-image');
            const altText = imageElement ? imageElement.getAttribute('alt') : '';
            if (!altText.includes("_ALL")) {
              item.style.display = 'none';
              item.classList.remove('media-viewer__item--single');
            } else {
              console.log("includes _ALL")
            }
          });

          this.thumbsItems.forEach((item) => {
            const imageElement = item.querySelector('img.img-fit');
            const altText = imageElement ? imageElement.getAttribute('alt') : '';

            if (!altText.includes("_ALL")) {
              item.style.display = 'none';
            }
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main) {
              if (item.main === this.currentItem) {
                currentItemIsVisible = true;
              }
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            // Find the first non-swatch item to activate
            const firstRealItem = selectedGroup.items.find(item => {
              const img = item.main.querySelector('img.product-image');
              return !img || !img.getAttribute('alt')?.toLowerCase().includes('swatch');
            });
            this.customSetActiveMedia((firstRealItem || selectedGroup.items[0]).main, true);
          }
          let thumbnail = 0;
          let mainPic = 0;
          for (const item of selectedGroup.items) {
            if (item.main) {
              mainPic++
            }
            if (item.thumb) {
              thumbnail++
            }
          }
          let mainThumbDif = mainPic - thumbnail;

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1 || mainThumbDif == 1) {
            for (const item of selectedGroup.items) {
              item.main.classList.add('media-viewer__item--single'); 
            }
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }


    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      // 1. Check if the thumbnails container even exists
      if (!this.thumbs) return;

      // 2. Attempt to find the thumbnail element
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );

      // 3. CRITICAL GUARD: If currentThumb is null, stop here!
      if (!this.currentThumb) {
        console.warn(`Thumbnail not found for media ID: ${this.currentItem.dataset.mediaId}`);
        return; 
      }
      const btn = this.currentThumb.querySelector('button');

      if (btn) {
        this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
          el.classList.remove('is-active');
          el.removeAttribute('aria-current');
        });
  
        btn.classList.add('is-active');
        btn.setAttribute('aria-current', 'true');
        this.checkThumbVisibilty(this.currentThumb);
      }
    }


    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {

      let thumbLtrPos = (/__top/.test(this.querySelector('.media-gallery__thumbs').className) || /__bottom/.test(this.querySelector('.media-gallery__thumbs').className));
      
      if (!window.theme.mediaMatches.lg) {
        thumbLtrPos = true;
      }

      const scrollPos =  (thumbLtrPos) ? this.thumbs.scrollLeft : this.thumbs.scrollTop;
      const lastVisibleThumbOffset = (thumbLtrPos) ? this.thumbs.clientWidth + scrollPos : this.thumbs.clientHeight + scrollPos;
      const thumbOffset = (thumbLtrPos) ? thumb.offsetLeft : thumb.offsetTop;
      const thumbClient = (thumbLtrPos) ? thumb.clientWidth + 13 : thumb.clientHeight + 13;

      if (thumbOffset + thumbClient > lastVisibleThumbOffset ) {
        (thumbLtrPos) ? this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' }) : this.thumbs.scrollTo({ top: thumbOffset, behavior: 'smooth' });
      } else if ((thumbOffset - 13) < scrollPos) {
        (thumbLtrPos) ? this.thumbs.scrollTo({ left: (thumbOffset - scrollPos), behavior: 'smooth' }) : this.thumbs.scrollTo({ top: ((thumbOffset) - scrollPos), behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);

}

customElements.whenDefined('media-gallery').then(() => {
  if (window.innerWidth < 1260) return; // Skip arrow logic on mobile

  document.addEventListener('DOMContentLoaded', () => {
    const container = document.querySelector('.media__tumbs--container');
    const thumbs = container?.querySelector('.media-thumbs');
    const arrowUp = container?.querySelector('.scroll-hint-up');
    const arrowDown = container?.querySelector('.scroll-hint-down');

    if (!container || !thumbs || !arrowUp || !arrowDown) return;

    const getScrollStep = () => {
      const thumbItem = thumbs.querySelector('.media-thumbs__item');
      if (!thumbItem) return 100; // fallback
      const style = window.getComputedStyle(thumbItem);
      const marginBottom = parseFloat(style.marginBottom || '0');
      const gap = parseFloat(style.gap || '0'); // fallback if margin isn't used
      const thumbHeight = thumbItem.offsetHeight + marginBottom || gap;

      const visibleCount = Math.floor(thumbs.clientHeight / thumbHeight);
      return visibleCount * thumbHeight;
    };

    const updateArrows = () => {
      const scrollTop = thumbs.scrollTop;
      const scrollHeight = thumbs.scrollHeight;
      const clientHeight = thumbs.clientHeight;
    
      const threshold = 5; // small buffer to catch near-zero offsets
    
      const roomAbove = scrollTop > threshold;
      const roomBelow = scrollTop + clientHeight < scrollHeight - threshold;
    
      container.classList.toggle('scroll-show-up', roomAbove);
      container.classList.toggle('scroll-show-down', roomBelow);
    };
    
    arrowUp.addEventListener('click', (e) => {
      e.preventDefault();
      const scrollStep = getScrollStep();
      thumbs.scrollBy({ top: -scrollStep, behavior: 'smooth' });
      // setTimeout(updateArrows, 250);
    });

    arrowDown.addEventListener('click', (e) => {
      e.preventDefault();
      const scrollStep = getScrollStep();
      thumbs.scrollBy({ top: scrollStep, behavior: 'smooth' });
      // setTimeout(updateArrows, 250);
    });

    requestAnimationFrame(() => {
      updateArrows();
      thumbs.addEventListener('scroll', updateArrows);
      window.addEventListener('resize', updateArrows);
    });
  });
});


document.querySelector('.scroll-hint-down')?.addEventListener('click', () => {
  thumbs.scrollBy({ top: 100, behavior: 'smooth' });
});
document.querySelector('.scroll-hint-up')?.addEventListener('click', () => {
  thumbs.scrollBy({ top: -100, behavior: 'smooth' });
});
